import React, { Dispatch, SetStateAction } from 'react';
import { useIntl } from 'react-intl';

import StarRating, { Variant } from '@/components/route/search/StarRating/StarRating';
import { parseIntSafe } from '@/utility/dataParser';

import css from './CategoryRating.module.css';
interface ICategoryRating {
  title: string;
  min: number;
  setFunc: (value: number) => void;
  required?: boolean;
  onError?: boolean;
  setErrorFunc?: Dispatch<SetStateAction<boolean>>;
  className?: string;
  starVariant?: string;
  outlineForUnselected?: boolean;
  /** Suppress the "TERRIBLE/BAD/OKAY/GOOD/GREAT" label rendered next to the stars once rated. */
  hideRatingText?: boolean;
}

const DEFAULT_RATING = 0;

const CategoryRating: React.FC<ICategoryRating> = ({
  title,
  min,
  setFunc,
  required = false,
  onError = false,
  setErrorFunc,
  className,
  starVariant,
  outlineForUnselected = false,
  hideRatingText = false,
}) => {
  const intl = useIntl();
  const ratingText = [
    intl.formatMessage({ defaultMessage: 'TERRIBLE', id: 'HXS+Vf' }),
    intl.formatMessage({ defaultMessage: 'BAD', id: 'ZxBv9I' }),
    intl.formatMessage({ defaultMessage: 'OKAY', id: 'g5D0Z4' }),
    intl.formatMessage({ defaultMessage: 'GOOD', id: 'lZLowS' }),
    intl.formatMessage({ defaultMessage: 'GREAT', id: '/dQuI5' }),
  ];

  const onRatingClick = (
    e: React.MouseEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>,
  ) => {
    const rating = parseIntSafe(e.currentTarget.value);
    if (setErrorFunc) setErrorFunc(false);
    if (rating != null) setFunc(rating);
  };

  return (
    <div
      className={`flex flex-col w-full mb-2 font-medium xs:justify-between xs:flex-row ${className}`}>
      {!!title && <p className="flex -mr-2 text-base text-gray-800">{title}</p>}
      <div className="flex flex-row justify-end my-2 text-sm sx:mb-2 xs:mt-0">
        {min > 0 && !hideRatingText && (
          <p data-testid="rating-label" className="text-gray-800 mr-1.5">
            {ratingText[min - 1]}
          </p>
        )}
        <StarRating
          minRating={min}
          onClick={onRatingClick}
          disabledMin={DEFAULT_RATING}
          variant={starVariant || Variant.green}
          outlineForUnselected={outlineForUnselected}
          className={`-mt-1 ${onError ? css.errorInput : ''}`}
          required={required}
        />
      </div>
    </div>
  );
};

export default CategoryRating;
